import React, { useRef, useEffect } from 'react';
import { LifeBuoy, MessageCircle, HeartPulse, HandHeart, Globe, TriangleAlert } from 'lucide-react';
import Navigation from './Navigation';
import MorningTide from './MorningTide';
import Footer from './Footer';
import './CrisisResources.css';

const resources = [
  {
    Icon: LifeBuoy,
    name: '988 Suicide & Crisis Lifeline',
    number: '988',
    info: 'Free and confidential, 24/7. Call or text for support during a suicidal crisis or emotional distress. Chat online if you prefer not to call.',
    color: '#15803d',
    accent: 'linear-gradient(90deg, #4ade80, #22c55e)',
    btns: [
      { label: 'Call 988', href: 'tel:988', color: 'green' },
      { label: 'Text 988', href: 'sms:988', color: 'blue' },
      { label: 'Chat Online', href: 'https://988lifeline.org/chat', color: 'purple' },
    ],
  },
  {
    Icon: MessageCircle,
    name: 'Crisis Text Line',
    number: 'Text HOME to 741741',
    info: 'Free, 24/7 crisis support over text. Trained crisis counselors are ready whenever you need them.',
    color: '#1d4ed8',
    accent: 'linear-gradient(90deg, #60a5fa, #3b82f6)',
    btns: [
      { label: 'Text HOME to 741741', href: 'sms:741741?&body=HOME', color: 'blue' },
    ],
  },
  {
    Icon: HeartPulse,
    name: 'SAMHSA National Helpline',
    number: '1-800-662-4357',
    info: 'Free, confidential, 24/7 treatment referral and information for mental health and substance use disorders. English and Spanish.',
    color: '#c2410c',
    accent: 'linear-gradient(90deg, #f97316, #ea580c)',
    btns: [
      { label: 'Call Now', href: 'tel:18006624357', color: 'green' },
    ],
  },
  {
    Icon: HandHeart,
    name: 'Trevor Project',
    number: '1-866-488-7386',
    info: 'Crisis intervention and suicide prevention for LGBTQ+ young people ages 13–24. Confidential, non-judgmental support.',
    color: '#be185d',
    accent: 'linear-gradient(90deg, #f472b6, #ec4899)',
    btns: [
      { label: 'Call', href: 'tel:18664887386', color: 'green' },
      { label: 'Text START to 678-678', href: 'sms:678678?&body=START', color: 'blue' },
    ],
  },
  {
    Icon: Globe,
    name: 'International Resources',
    number: 'findahelpline.com',
    info: 'Outside the United States? Find a verified crisis helpline in your country. Help is available worldwide.',
    color: '#6d28d9',
    accent: 'linear-gradient(90deg, #a78bfa, #7c3aed)',
    btns: [
      { label: 'Visit Site', href: 'https://findahelpline.com', color: 'purple' },
    ],
  },
];

/**
 * Pointer-tracking 3D tilt adapted from the react-bits ProfileCard.
 * Drives CSS custom properties on the card via a smoothed rAF spring —
 * we keep the interactivity but not the holographic rainbow gradient.
 */
const useTilt = (ref) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Skip on touch / no-hover devices — tilt is a pointer affordance.
    if (window.matchMedia('(hover: none)').matches) return;

    let raf = null;
    let cx = 50, cy = 50, tx = 50, ty = 50;

    const apply = () => {
      el.style.setProperty('--pointer-x', `${cx}%`);
      el.style.setProperty('--pointer-y', `${cy}%`);
      el.style.setProperty('--rotate-x', `${((cy - 50) / 6).toFixed(2)}deg`);
      el.style.setProperty('--rotate-y', `${(-(cx - 50) / 6).toFixed(2)}deg`);
      const dist = Math.min(Math.hypot(cx - 50, cy - 50) / 50, 1);
      el.style.setProperty('--pointer-from-center', dist.toFixed(3));
    };

    const loop = () => {
      cx += (tx - cx) * 0.15;
      cy += (ty - cy) * 0.15;
      apply();
      if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
        raf = requestAnimationFrame(loop);
      } else {
        raf = null;
      }
    };

    const start = () => { if (!raf) raf = requestAnimationFrame(loop); };

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width) * 100;
      ty = ((e.clientY - r.top) / r.height) * 100;
      start();
    };
    const onEnter = () => el.classList.add('tilt-active');
    const onLeave = () => {
      tx = 50;
      ty = 50;
      el.classList.remove('tilt-active');
      start();
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointerleave', onLeave);

    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointerleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref]);
};

const CrisisCard = ({ r }) => {
  const ref = useRef(null);
  useTilt(ref);
  const { Icon } = r;

  return (
    <div className="crisis-card-outer">
      <div
        ref={ref}
        className="crisis-resource-card"
        style={{ '--card-accent': r.accent, '--card-color': r.color }}
      >
        <div className="crisis-card-glow" />
        <div className="crisis-card-body">
          <div className="crisis-card-header">
            <div className="crisis-card-icon">
              <Icon size={26} strokeWidth={1.75} />
            </div>
            <h3 className="crisis-card-name">{r.name}</h3>
          </div>
          <p className="crisis-card-number">{r.number}</p>
          <p className="crisis-card-info">{r.info}</p>
          <div className="crisis-card-btns">
            {r.btns.map((b, j) => (
              <a
                key={j}
                href={b.href}
                className={`crisis-chip ${b.color}`}
                target={b.href.startsWith('http') ? '_blank' : undefined}
                rel={b.href.startsWith('http') ? 'noopener noreferrer' : undefined}
              >
                {b.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CrisisResources = () => {
  return (
    <div className="crisis-page">
      <MorningTide />
      <Navigation />

      {/* Hero */}
      <div className="crisis-hero">
        <span className="crisis-hero-eyebrow">You are not alone</span>
        <h1 className="crisis-hero-heading">
          It's okay to ask<br /><span>for help.</span>
        </h1>
        <p className="crisis-hero-sub">
          If you're going through something difficult, real support is just a call or text away.
          These resources are free, confidential, and available right now.
        </p>
        <a href="tel:911" className="crisis-911-btn">
          <TriangleAlert size={22} strokeWidth={2.25} />
          Immediate danger? Call 911
        </a>
      </div>

      {/* Resource Cards */}
      <section className="crisis-resources-section">
        <p className="crisis-resources-heading">Crisis Helplines & Support</p>
        <div className="crisis-grid">
          {resources.slice(0, 3).map((r, i) => (
            <CrisisCard key={i} r={r} />
          ))}
        </div>
        <div className="crisis-grid crisis-grid--centered">
          {resources.slice(3).map((r, i) => (
            <CrisisCard key={i + 3} r={r} />
          ))}
        </div>
      </section>

      {/* Footer note */}
      <div className="crisis-footer-note">
        <p>
          <strong>Echo is an AI companion</strong> — not a replacement for professional care.
          If you're in crisis, please reach out to one of the resources above.
          You deserve real, human support. 💙
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default CrisisResources;
